import { InstrumentationData } from '../instrumentation/instrumentation-data.model';
import { InstrumentationParam, InstrumentationParams, KnownInstrumentationActions, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { PathArrayFormatter } from '../utilities/path-array-formatter.utility';
import { UrlEncoder } from '../utilities/url-encoder.utility';
import { UrlParser } from '../utilities/url-parser.utility';
import { AttachmentType } from './email-attachment.model';
import { KnownWorkloadReferralNames } from './known-workload-referral-names.model';
import { ListItem } from './list-item.model';
import { Person } from './person.model';

//NOTE: If editing this enum, ensure to also edit fileTypeNameFromType() below
export enum FileType {
    Unknown = 0,
    None,
    Word,
    Excel,
    PowerPoint,
    Pdf,
    Audio,
    Video,
    OneNote,
    OneNotePage,
    Visio,
    Photos,
    Text,
    Outlook,
    Sway,
    OneNoteClassNoteBook,
    Forms,
    FormsQuiz,
    Project,
    ProjectOperations,
    Portfolio,
    NewsArticle,
    OneDrive,
    SharePoint,
    Fluid,
    FluidActivityBlueprint,
    PowerBI,
    StreamsVideo,
    List, // This is currently applicable to Edgeworth items and Create Page Tile since we get SharePoint Lists
    UpcomingMeeting,
    Yammer,
    Designer,
    // Below are template types and not actual file types.
    VisioBusinessWac,
    VisioFlowChartWac,
    VisioGeneralWac,
    VisioNetworkWac,
    VisioSoftwareWac,
    ThirdParty,
    Trello,
    VisioStartWac,
    Stream, // For ODSP stream 2.0 videos
    MindMeister,
    Monday,
    Platform,
    Whiteboard,
    MeetingNotes, // OneNote meeting notes, created together with an Outlook meeting
    MyAnalytics,
    MeetingObject,
    PlayLists,
    PlayList,
    ScreenRecording,
    Clipchamp
}

export enum SourceProvenance {
    Unknown = 0,
    OfficeGraph,
    SharePoint,
    Exchange,
    OfficeMru,
    AADGraph,
    OneDriveBusiness,
    Substrate
}

export const enum FileItemCategory {
    DocOneDrive = 'DocOneDrive',
    DocSharePoint = 'DocSharePoint',
    Sway = 'Sway'
}

export const OneDriveBusinessUrlRegex = /http[s]?:\/\/[\S]*?-my\.sharepoint[\S]*?\.com/;
export const SharePointUrlRegex = /http[s]?:\/\/[\S]*?\.sharepoint[\S]*?\.com/;
export const OneDriveConsumerUrlRegex = /http[s]?:\/\/d\.docs\.live\.net/;

export class FileItem implements ListItem {
    private static readonly NameToFileType: { [name: string]: FileType; } = {
        'none': FileType.None,
        'unknown': FileType.Unknown,
        'excel': FileType.Excel,
        'onenote': FileType.OneNote,
        'pdf': FileType.Pdf,
        'powerpoint': FileType.PowerPoint,
        'audio': FileType.Audio,
        'video': FileType.Video,
        'visio': FileType.Visio,
        'word': FileType.Word,
        'photos': FileType.Photos,
        'text': FileType.Text,
        'outlook': FileType.Outlook,
        'sway': FileType.Sway,
        'forms': FileType.Forms,
        'formsquiz': FileType.FormsQuiz,
        'project': FileType.Project,
        'projectoperations': FileType.ProjectOperations,
        'portfolio': FileType.Portfolio,
        'news': FileType.NewsArticle,
        'newsarticle': FileType.NewsArticle,
        'onedrive': FileType.OneDrive,
        'sharepoint': FileType.SharePoint,
        'bohemia': FileType.Fluid,
        'fluid': FileType.Fluid,
        'loop': FileType.Fluid,
        'loop components': FileType.Fluid,
        'powerbi': FileType.PowerBI,
        'streamsvideo': FileType.StreamsVideo,
        'yammer': FileType.Yammer,
        'mindmeister': FileType.MindMeister,
        'stream': FileType.Stream,
        'monday': FileType.Monday,
        'trello': FileType.Trello,
        'platform': FileType.Platform,
        'whiteboard': FileType.Whiteboard,
        'meetingnotes': FileType.MeetingNotes,
        'myanalytics': FileType.MyAnalytics,
        'list': FileType.List,
        'playlists': FileType.PlayLists,
        'playlist': FileType.PlayList,
        'meeting object': FileType.MeetingObject,
        'upcomingmeeting': FileType.UpcomingMeeting,
        'screenrecording': FileType.ScreenRecording,
        'designer' : FileType.Designer,
        'clipchamp' : FileType.Clipchamp
    };

    public static NameToProvenance: { [name: string]: SourceProvenance; } = {
        'Unknown': SourceProvenance.Unknown,
        'AADGraph': SourceProvenance.AADGraph,
        'Exchange': SourceProvenance.Exchange,
        'OfficeGraph': SourceProvenance.OfficeGraph,
        'OfficeMru': SourceProvenance.OfficeMru,
        'SharePoint': SourceProvenance.SharePoint,
        'Substrate': SourceProvenance.Substrate
    };

    private static extensionToFileType: { [extension: string]: FileType };
    private static fileTypeStringToExtensions: { [fileType: string]: Array<string> };
    private static StreamExtensions: Array<string> = ['mts', 'm2ts', 'ts', '3g2', '3gp', '3gp2', '3gpp', 'asf', 'wmv',
        'avi', 'm4v', 'mov', 'mp4', 'mp4v', 'ogg', 'webm', 'movie', 'mkv', 'mpg', 'mpeg'];

    public fileTypeName: string;
    public provenanceName: string;
    public documentIcon: string;
    public attribution: string;
    public attributionUrl: string;
    public category: FileItemCategory;

    /**
    * Creates a new FileItem
    * @param id                         The id of the document, used for instrumentation purposes.
    * @param impressionId               The impression id assigned to the group of items returned from server.
    * @param docId                      The unique id of the document.
    * @param fileName                   The file name of the document.
    * @param title                      The title of the document.
    * @param url                        The url by which the document can be accessed.
    * @param webUrl                     The url by which the document can be accessed in consumer to prevent redirects.
    * @param downloadUrl                The url by which the document can be downloaded.
    * @param lastModifiedTime           When the item was last modified.
    * @param lastViewedTime             When the item was last viewed.
    * @param sharedTime                 When the item was shared.
    * @param fileType                   The file type of document.
    * @param provenance                 Service that the document is sourced from.
    * @param pathArray                  The string components that concatenated form the path of the document.
    * @param isRtl                      Whether the current culture requires rtl
    * @param urlEncoded                 The flag indicating that the url and downloadUrl are already encoded
    * @param lastModifiedBy             The person who last modified the file.
    * @param containerUrl               The url of the container for the document.
    * @param isEmailAttachment          Whether the item is an email attachment.
    * @param attachmentType             Which type of attachment the item is.
    * @param isLocal                    Whether the item is local.
    */
    constructor(
        public id: string,
        public impressionId: string,
        public docId: string,
        public driveId: string,
        public fileName: string,
        public title: string,
        public url: string,
        public webUrl: string,
        public downloadUrl: string,
        public lastModifiedTime: Date,
        public lastViewedTime: Date,
        public sharedTime: Date,
        public fileType: FileType,
        public provenance: SourceProvenance,
        public pathArray: Array<string>,
        isRtl: boolean,
        urlEncoded: boolean = true,
        public lastModifiedBy: Person = null,
        containerUrl: string = null,
        isEmailAttachment: boolean = false,
        attachmentType: AttachmentType = AttachmentType.None,
        public isLocal: boolean = false,
        public isSafeLink: boolean = false
    ) {
        this.fileTypeName = FileItem.fileTypeNameFromType(this.fileType);
        this.provenanceName = FileItem.provenanceNameFromType(this.provenance);
        this.documentIcon = FileItem.documentIconFromType(this.fileType);

        // skip any url modification if it is an Outlook safe link
        if (url && !isSafeLink) {
            let docUrl: string = url;
            // We want links to editable Office documents to open in the WAC
            if (!isLocal && (!isEmailAttachment || (isEmailAttachment && attachmentType === AttachmentType.Modern))
                && (fileType === FileType.Word || fileType === FileType.Excel || fileType === FileType.OneNote || fileType === FileType.PowerPoint || fileType === FileType.Visio || fileType === FileType.Fluid || fileType === FileType.Stream)) {
                // 'web=1' is a valid file/folder name, so you need to be careful when checking if this parameter already exists.
                // recommended sometimes adds the web=1 query param, but to keep things simple we will just add another one.
                const webQueryParam = 'web=1';
                const queryParamString = url.indexOf('?') === -1 ? '?' : '&';  // This relies on the expectation that '?' is not a valid character in file or folder names.
                docUrl = url + queryParamString + webQueryParam;
            }

            // Don't encode local file paths
            // encodeUrl decodes '=' and doesn't re-encode them. we need '=' to be encoded for email attachments
            if (!isEmailAttachment && !isLocal) {
                // Todo: Remove when MRU send links that are RFC complient and uses hashes for solely hash anchors and encodes hashes that are not used for such purposes.
                this.url = this.encodeUrl(docUrl, urlEncoded, this.fileType === FileType.Forms || this.fileType === FileType.Portfolio || this.fileType === FileType.Project || this.fileType === FileType.ProjectOperations);
            } else {
                this.url = docUrl;
            }
        }

        if (this.downloadUrl) {
            this.downloadUrl = this.encodeUrl(this.downloadUrl, urlEncoded);
        }

        this.setupAttribution(containerUrl, isRtl);
    }

    public static setStreamExtensions(extensions?: Array<string>) {
        if (extensions?.length > 0) {
            FileItem.StreamExtensions = extensions;

            if (!FileItem.fileTypeStringToExtensions) {
                FileItem.createFileTypeStringToExtensionsValues();
            }
            FileItem.fileTypeStringToExtensions[FileType[FileType.Stream]] = FileItem.StreamExtensions;

            FileItem.createExtensionToFileTypeValues();
        }
    }

    public static fileTypeToApp(fileType: FileType) {
        if (fileType === FileType.Forms || fileType === FileType.FormsQuiz) {
            return FileType.Forms;
        } else {
            return fileType;
        }
    }

    public static fileTypeFromExtension(extension: string): FileType {
        if (!FileItem.extensionToFileType) {
            FileItem.createExtensionToFileTypeValues();
        }

        if (!extension) {
            return FileType.None;
        }

        return FileItem.extensionToFileType[extension.toLowerCase()] || FileType.Unknown;
    }

    public static fileExtensionsForType(fileType: FileType): Array<string> {
        if (!fileType || fileType === FileType.None) {
            return [];
        }

        if (!FileItem.fileTypeStringToExtensions) {
            FileItem.createFileTypeStringToExtensionsValues();
        }

        return FileItem.fileTypeStringToExtensions[FileType[fileType]] || [];
    }

    public static distributionFromFileTypes(items: Array<FileType>): string {
        if (!items) {
            return '';
        }

        return items.reduce((combined, current, index) => combined + (index === 0 ? '' : ';') + FileItem.fileTypeNameFromType(current), '');
    }

    public static distributionFromFileItems(items: Array<FileItem>): string {
        if (!items) {
            return '';
        }

        return FileItem.distributionFromFileTypes(items.map(item => item.fileType));
    }

    // Used to generate the css class for document sprite images
    public static fileIconNameFromType(fileType: FileType): string {
        switch (fileType) {
            case FileType.Word:
                return 'Word';
            case FileType.Excel:
                return 'Excel';
            case FileType.PowerPoint:
                return 'PowerPoint';
            case FileType.OneNote:
                return 'OneNote';
            case FileType.Pdf:
                return 'PDF';
            case FileType.Visio:
                return 'Visio';
            case FileType.Sway:
                return 'Sway';
            case FileType.Forms:
                return 'Forms';
            case FileType.Project:
                return 'Project';
            case FileType.ProjectOperations:
                return 'ProjectOperations';
            case FileType.Portfolio:
                return 'Portfolio';
            case FileType.Fluid:
            case FileType.FluidActivityBlueprint:
                return 'Fluid';
            case FileType.PowerBI:
                return 'PowerBI';
            case FileType.StreamsVideo:
            case FileType.Stream:
            case FileType.ScreenRecording:
                return 'StreamsVideo';
            case FileType.Video:
                return 'Video';
            case FileType.List:
                return 'List';
            case FileType.PlayLists:
                return 'PlayLists';
            case FileType.PlayList:
                return 'PlayList';
            case FileType.Trello:
                return 'Trello';
            case FileType.NewsArticle:
                return 'NewsArticle';
            case FileType.Whiteboard:
                return 'Whiteboard';
            case FileType.MeetingNotes:
                return 'MeetingNotes';
            case FileType.Photos:
                return 'Photos';
            case FileType.MeetingObject:
                return 'MeetingObject';
            default:
                return 'Document';
        }
    }

    // TODO: Localize the values that are displayed before we start showing new file types
    // Used for document icon hover titles, collection keys, and setting the Shell title on WAC start pages.
    // For valid document types, should be inverse of NameToFileType dictionary (minus None or Unknown)
    public static fileTypeNameFromType(fileType: FileType): string {
        switch (fileType) {
            case FileType.Word:
                return 'Word';
            case FileType.Excel:
                return 'Excel';
            case FileType.PowerPoint:
                return 'PowerPoint';
            case FileType.OneNote:
            case FileType.OneNoteClassNoteBook:
            case FileType.MeetingNotes:
                return 'OneNote';
            case FileType.Pdf:
                return 'PDF';
            case FileType.Visio:
                return 'Visio';
            case FileType.Audio:
                return 'Audio';
            case FileType.Video:
                return 'Video';
            case FileType.Photos:
                return 'Photos';
            case FileType.Outlook:
                return 'Outlook';
            case FileType.Text:
                return 'Text';
            case FileType.Sway:
                return 'Sway';
            case FileType.Forms:
            case FileType.FormsQuiz:
                return 'Forms';
            case FileType.Project:
                return 'Project';
            case FileType.ProjectOperations:
                return 'ProjectOperations';
            case FileType.Portfolio:
                return 'Portfolio';
            case FileType.NewsArticle:
                return 'NewsArticle';
            case FileType.Fluid:
                return 'Loop';
            case FileType.PowerBI:
                return 'PowerBI';
            case FileType.StreamsVideo:
                return 'Streams';
            case FileType.Stream:
                return 'Stream';
            case FileType.ScreenRecording:
                return 'ScreenRecording';
            case FileType.UpcomingMeeting:
                return 'UpcomingMeeting';
            case FileType.List:
                return 'List';
            case FileType.PlayLists:
                return 'PlayLists';
            case FileType.PlayList:
                return 'PlayList';
            case FileType.MindMeister:
                return 'MindMeister';
            case FileType.Monday:
                return 'monday.com';
            case FileType.Trello:
                return 'Trello';
            case FileType.Platform:
                return 'Platform';
            case FileType.Whiteboard:
                return 'Whiteboard';
            case FileType.MeetingObject:
                return 'Meeting Object';
            case FileType.MyAnalytics:
                return 'MyAnalytics';
            case FileType.Designer:
                return 'Designer';
            case FileType.Clipchamp:
                return 'Clipchamp';
            default:
                return 'Document';
        }
    }

    public static fileTypeNameToFileType(fileTypeName: string): FileType {
        if (!fileTypeName) {
            return FileType.Unknown;
        }
        if (fileTypeName === 'Custom') {
            return FileType.ThirdParty;

        }
        return FileItem.NameToFileType[fileTypeName.toLowerCase()] || FileType.Unknown;
    }

    public static fileTypeFromFileName(fileName: string): FileType {
        return FileItem.fileTypeFromExtension(FileItem.getFileExtension(fileName));
    }

    public static provenanceNameFromType(provenanceType: SourceProvenance): string {
        switch (provenanceType) {
            case SourceProvenance.AADGraph:
                return 'AADGraph';
            case SourceProvenance.Exchange:
                return 'Exchange';
            case SourceProvenance.OfficeGraph:
                return 'OfficeGraph';
            case SourceProvenance.OfficeMru:
                return 'OfficeMru';
            case SourceProvenance.SharePoint:
                return 'SharePoint';
            case SourceProvenance.Substrate:
                return 'Substrate';
            default:
                return 'Unknown';
        }
    }

    public static getFileExtension(fileName: string) {
        if (!fileName) {
            return '';
        }
        const extensionIndex = fileName.lastIndexOf('.');
        return extensionIndex === -1 ? '' : fileName.substring(extensionIndex + 1);
    }

    public static getFileExtensionFromUrl(url: string): string {
        if (!url) {
            return '';
        }

        const lastIndexOfPeriod = url.lastIndexOf('.');

        if (lastIndexOfPeriod === -1) {
            return '';
        }

        return url.substring(lastIndexOfPeriod + 1).split('?')[0];
    }

    public static documentIconFromType(fileType: FileType, iconSize: '64x64' | '32x32' | '20x20' | '16x16' = '32x32'): string {
        // TODO: once Project and Portfolio svg icons are available, remove this if statement and remove the document-sprite.png resource.
        if (fileType === FileType.Portfolio) {
            return `document-icon-sprite document-icon-sprite-${FileItem.fileIconNameFromType(fileType).toLowerCase()}-${iconSize}`;
        }

        return `document-icon--${iconSize} document-icon--${FileItem.fileIconNameFromType(fileType).toLowerCase()}`;
    }

    public static referralFromFileType(fileType: FileType): string {
        switch (fileType) {
            case FileType.Word:
                return KnownWorkloadReferralNames.Word;
            case FileType.Excel:
                return KnownWorkloadReferralNames.Excel;
            case FileType.PowerPoint:
                return KnownWorkloadReferralNames.PowerPoint;
            case FileType.OneNote:
                return KnownWorkloadReferralNames.OneNote;
            case FileType.Sway:
                return KnownWorkloadReferralNames.Sway;
            case FileType.Visio:
                return KnownWorkloadReferralNames.Visio;
            case FileType.Project:
            case FileType.ProjectOperations:
            case FileType.Portfolio:
                return KnownWorkloadReferralNames.Project;
            case FileType.PowerBI:
                return KnownWorkloadReferralNames.PowerBI;
            default:
                return FileItem.fileTypeNameFromType(fileType);
        }
    }

    public static referralFromFileLocation(url: string): string {
        if (url && url.length) {
            if (url.match(OneDriveBusinessUrlRegex) || url.match(OneDriveConsumerUrlRegex)) {
                return KnownWorkloadReferralNames.OneDrive;
            }

            if (url.match(SharePointUrlRegex)) {
                return KnownWorkloadReferralNames.SharePoint;
            }
        }

        return null;
    }

    public static isConsumerDocument(url: string): boolean {
        const hostname = UrlParser.getHostName(url);
        return hostname.lastIndexOf('live.net') > 0;
    }

    public static fileNameWithoutExtension(fileName: string): string {
        if (!fileName) {
            return fileName;
        }
        const extensionIndex = fileName.lastIndexOf('.');
        return extensionIndex === -1 ? fileName : fileName.substring(0, extensionIndex);
    }

    public documentIconWithSize(size: '64x64' | '32x32' | '20x20' | '16x16' = '32x32') {
        return FileItem.documentIconFromType(this.fileType, size);
    }

    // In Edgeworth, id property is the ID of document from API response, not UI element ID, so we use logIdAsDocumentId to determine the right param
    public getClickData(eventName: KnownInstrumentationEvents, action: KnownInstrumentationActions, index: number, logIdAsDocumentId: boolean = false): InstrumentationData {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.ImpressionId] = this.impressionId;
        if (logIdAsDocumentId) {
            params[InstrumentationParam.ElementId] = eventName;
            params[InstrumentationParam.DocumentId] = this.id;
        } else {
            params[InstrumentationParam.ElementId] = this.id;
        }
        params[InstrumentationParam.Referral] = FileItem.referralFromFileType(this.fileType);
        params[InstrumentationParam.Index] = index.toString();
        params[InstrumentationParam.DocumentType] = this.fileTypeName;
        params[InstrumentationParam.Action] = action;

        const timeSinceLastOpened = this.timeSinceLastOpenInSeconds();
        if (timeSinceLastOpened !== null) {
            params[InstrumentationParam.InactivityDuration] = timeSinceLastOpened.toString();
        }

        return {
            eventName,
            params,
            metrics: []
        };
    }

    public getClickDataForPath(
        eventName: KnownInstrumentationEvents,
        action: KnownInstrumentationActions,
        index: number
    ): InstrumentationData {
        const result = this.getClickData(eventName, action, index);

        if (result && result.params) {
            // Override referral param based on file path instead of file type
            result.params[InstrumentationParam.Referral] = FileItem.referralFromFileLocation(this.url);
        }

        return result;
    }

    public timeSinceLastOpenInSeconds(): number {
        if (this.lastViewedTime) {
            return Math.round((new Date().getTime() - this.lastViewedTime.getTime()) / 1000);
        }

        return null;
    }

    public getId(): string {
        return this.id;
    }

    private static createExtensionToFileTypeValues(): void {
        if (!FileItem.fileTypeStringToExtensions) {
            FileItem.createFileTypeStringToExtensionsValues();
        }
        FileItem.extensionToFileType = {};
        for (const typeName in FileItem.fileTypeStringToExtensions) {
            if (!FileItem.fileTypeStringToExtensions.hasOwnProperty(typeName)) {
                continue;
            }

            const extensions = FileItem.fileTypeStringToExtensions[typeName];
            if (!extensions) {
                continue;
            }
            extensions.forEach(ext => {
                FileItem.extensionToFileType[ext] = FileType[typeName];
            });
        }
    }

    private static createFileTypeStringToExtensionsValues(): void {
        // Note: Sway files don't have file extensions, so they are not included in this mapping.
        FileItem.fileTypeStringToExtensions = {};
        FileItem.fileTypeStringToExtensions[FileType[FileType.Excel]] = [
                'xls', 'xlsx', 'xlb', 'xlc', 'xlsb', 'xlsm', 'xlt', 'xltx', 'xltm', 'xlm', 'xla', 'xll', 'xlam', 'xlw',
                'excel', 'csv'
            ];
        FileItem.fileTypeStringToExtensions[FileType[FileType.Visio]] = [
                'vsd', 'vss', 'vst', 'vdx', 'vsx', 'vtx', 'vsl', 'vsu', 'vsw', 'vsdx', 'vsdm', 'vssm', 'vstx', 'vstm'
            ];
        FileItem.fileTypeStringToExtensions[FileType[FileType.PowerPoint]] = [
                'ppt', 'pptx', 'pot', 'potx', 'potm', 'ppam', 'ppsx', 'ppsm', 'sldx', 'sldm', 'ppa', 'pps', 'pptm',
                'powerpoint'
            ];
        FileItem.fileTypeStringToExtensions[FileType[FileType.Word]] = ['doc', 'dot', 'docx', 'docm', 'dotx', 'dotm', 'docb', 'word'];
        FileItem.fileTypeStringToExtensions[FileType[FileType.Photos]] = ['jpg', 'png', 'jpeg', 'gif', 'svg', 'bmp', 'tif', 'tiff', 'heic'];
        FileItem.fileTypeStringToExtensions[FileType[FileType.Video]] = FileItem.StreamExtensions;
        FileItem.fileTypeStringToExtensions[FileType[FileType.Outlook]] = ['msg', 'eml'];
        FileItem.fileTypeStringToExtensions[FileType[FileType.OneNote]] = ['one'];
        FileItem.fileTypeStringToExtensions[FileType[FileType.Pdf]] = ['pdf'];
        FileItem.fileTypeStringToExtensions[FileType[FileType.Audio]] = ['mp3'];
        FileItem.fileTypeStringToExtensions[FileType[FileType.Text]] = ['txt'];
        FileItem.fileTypeStringToExtensions[FileType[FileType.Fluid]] = ['fluid', 'loop'];
        FileItem.fileTypeStringToExtensions[FileType[FileType.Stream]] = FileItem.StreamExtensions;
        FileItem.fileTypeStringToExtensions[FileType[FileType.Whiteboard]] = ['whiteboard'];
        FileItem.fileTypeStringToExtensions[FileType[FileType.MeetingNotes]] = ['note'];
    }

    private encodeUrl(url: string, urlEncoded: boolean, encodeComponents?: boolean): string {
        try {
            let decodedUrl = url;
            // It's important to not decode an already decoded URL since file URLs can contain filenames like '%25.docx'.
            if (urlEncoded) {
                // Some of our services (MRU++ v2) return partially encoded URLs. Therefore, to fully encode them we first decode and then re-encode.
                decodedUrl = UrlEncoder.decodeUrl(url);
                if (!decodedUrl) {
                    // If there is an error, just assume the input url is already decoded. This error will happen when the input URL is already decoded and has '%' characters
                    decodedUrl = url;
                }
            }

            return UrlEncoder.encodeUrl(decodedUrl, encodeComponents);
        } catch (e) {
            return url;
        }
    }

    private setupAttribution(containerUrl: string, isRtl: boolean) {
        if (this.fileType === FileType.Sway || this.fileType === FileType.Forms || this.fileType === FileType.Project || this.fileType === FileType.ProjectOperations) {
            return;
        }

        this.attribution = PathArrayFormatter.Format(this.pathArray, isRtl)[1];
        if (!containerUrl) {
            const lastPathSeparator = this.url ? this.url.lastIndexOf(this.isLocal ? '\\' : '/') : -1;
            this.attributionUrl = lastPathSeparator === -1 ? this.url : this.url.substring(0, lastPathSeparator + 1);
            // Must add id=root to folder path for files at root of ODC drive
            if (this.attributionUrl && this.attributionUrl.match(OneDriveConsumerUrlRegex)) {
                const attributionPath = UrlParser.getLocation(this.attributionUrl).path;
                if (attributionPath.lastIndexOf('/', attributionPath.length - 2) <= 0) {
                    this.attributionUrl = `${this.attributionUrl}?id=root`;
                }
            }
        } else {
            this.attributionUrl = containerUrl;
        }
    }
}
